import { apiService } from '../api/api-client';
import { InventoryListParams, PaginatedInventoryList, InventoryItem, PaginatedInventoryStateList, InventoryState, AvailabilityReportItem, ForceCollectionResponse } from '@/types';

export const inventoryService = {
  async getInventoryList(params?: InventoryListParams): Promise<PaginatedInventoryList> {
    return apiService.get<PaginatedInventoryList>('/inventory/', { params });
  },
  
  async getInventoryCount(params?: Omit<InventoryListParams, 'limit' | 'offset'>): Promise<{ count: number }> {
    // Get count with minimal request (limit 1, offset 0)
    const response = await apiService.get<PaginatedInventoryList>('/inventory/', { 
      params: { ...params, limit: 1, offset: 0 } 
    });
    return { count: response.count };
  },
  
  // The backend caps ?limit (max 10000), so page through in cap-sized chunks —
  // a single ?limit=count would truncate a book larger than the cap. Whole-snapshot
  // callers (availability CSV, recall dialog) still get every row.
  async getAllInventory(params?: Omit<InventoryListParams, 'limit' | 'offset'>, count?: number): Promise<InventoryItem[]> {
    const pageSize = 10000; // == backend max_limit
    const collected: InventoryItem[] = [];
    let offset = 0;
    let total = count ?? Infinity;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const page = await apiService.get<PaginatedInventoryList>('/inventory/', {
        params: { ...params, limit: pageSize, offset },
      });
      collected.push(...page.results);
      total = count ?? page.count;
      offset += pageSize;
      if (page.results.length < pageSize || collected.length >= total) break;
    }
    return collected;
  },
  
  async getInventoryStates(): Promise<InventoryState[]> {
    const response = await apiService.get<PaginatedInventoryStateList>('/inventory/states/');
    return response.results;
  },
  
  async getAvailabilityReport(state?: number): Promise<AvailabilityReportItem[]> {
    const params = state ? { state } : {};
    return apiService.get<AvailabilityReportItem[]>('/inventory/availability-report/', { params });
  },
  
  async forceCollectInventory(): Promise<ForceCollectionResponse> {
    return apiService.post<ForceCollectionResponse>('/inventory/force-collect/');
  },
  async getCollectionInfo(): Promise<{
    is_running: boolean;
    has_rates_file: boolean;
    has_velocity_positions_file: boolean;
  }> {
    return apiService.get<{
      is_running: boolean;
      has_rates_file: boolean;
      has_velocity_positions_file: boolean;
    }>('/inventory/collection-info/');
  },
};
